/**
 * @module product
 * @description Product domain entry point — orchestrates the PDP (Product Detail Page).
 *
 * Composes several sub-modules into a cohesive product experience:
 * - ProductGallery (image slider / zoom)
 * - ProductOptions (variant selection, combined listings)
 * - AddToCart (AJAX add-to-cart on the product form — no document-level delegation)
 * - QuantitySpinner, Timer, RecentlyViewedStorage
 *
 * Loaded via `<script type="module">` on product templates.
 * Not in the importmap — consumed as a page-level entry point only.
 *
 * @see {@link @stardust/product-gallery}
 * @see {@link @stardust/product-options}
 * @see {@link @stardust/add-to-cart}
 */
import ProductGallery from "@stardust/product-gallery";
import ProductOptions from "@stardust/product-options";
import AddToCart from "@stardust/add-to-cart";
import { formatMoney, getThemeSettings, onCopySkuClick } from "@stardust/utilities";
import { RecentlyViewedStorage } from "@stardust/recently-viewed";
import { normalizeSectionId, sectionRenderer } from "@stardust/section-renderer";
import { preserveYotpoWidgetOnMorph } from "@stardust/yotpo";
import Timer from "@stardust/timer";
import "@stardust/option-guide-drawer";

class ProductSection {
  constructor(element) {
    this.element = element;
    this.timers = [];
    // Monotonic token — every option change supersedes the one before it. An
    // in-flight chain compares its captured token against this before touching
    // history, navigating, or re-initializing, so only the newest click wins.
    this.renderToken = 0;
    // Section IDs the most recent option change handed to sectionRenderer, so the
    // next one can abort the whole set rather than just the section it re-renders.
    this.pendingSectionIds = [];
    this.shopPayProxyBtn = null;
    this.shopPayProxyClickHandler = null;
    this.shopPayWireRun = 0;
    this.skuEl = null;
    this.boundCopySku = null;
    // Bind event listeners once (these survive morphs because element is the section root)
    element.addEventListener("product:option-change", (e) => {
      this.handleOptionChange(e.detail);
    });

    this.init();
  }

  init() {
    const { element } = this;
    const productId = element.dataset.productId;
    const productHandle = element.dataset.productHandle;

    // Track recently viewed product
    if (productHandle) {
      RecentlyViewedStorage.add(productHandle);
    }

    // Product options (variant selection)
    const optionsEl = element.querySelector("[data-buy-box-options]");
    if (optionsEl) {
      this.options = new ProductOptions(optionsEl);
    }

    // Add to cart — bind to the form that wraps the primary ATC control. Multiple
    // `{% form 'product' %}` blocks exist (financing, etc.); the first matching form
    // is not necessarily the one that contains [data-add-to-cart].
    const atcBtn = element.querySelector("[data-add-to-cart]");
    const form = atcBtn?.closest("form");
    if (form) {
      this.addToCart = new AddToCart({ form });
    }

    // Gallery
    const galleryEl = element.querySelector("[data-product-gallery]");
    if (galleryEl) {
      this.gallery = new ProductGallery(galleryEl);
    }

    // Init Timer
    const timerEls = element.querySelectorAll("[data-timer]");
    timerEls.forEach((timerEl) => {
      this.timers.push(new Timer(timerEl));
    });

    // Shop Pay financing "Learn more" proxy button
    this.#wireShopPayLearnMore();

    // Register SKU copy handler
    if (this.skuEl && this.boundCopySku) {
      this.skuEl.removeEventListener("click", this.boundCopySku);
    }
    this.skuEl = element.querySelector("[data-product-sku]");
    if (this.skuEl) {
      this.boundCopySku = (event) => onCopySkuClick(event, this.element);
      this.skuEl.addEventListener("click", this.boundCopySku);
    }
  }

  /**
   * Reinitialize all internal components after a DOM morph.
   * Called when the Section Rendering API has replaced DOM nodes,
   * invalidating any previously-bound event listeners.
   */
  reinit() {
    // Tear down instances that have explicit cleanup
    this.options?.destroy?.();
    this.gallery?.destroy?.();
    this.addToCart?.destroy?.();
    this.timers.forEach((timer) => timer.stop());
    this.timers = [];

    // Re-bind everything from the (now-morphed) DOM
    this.init();
  }

  #getPrimarySectionId() {
    const wrap = this.element.closest('[id^="shopify-section-"]');
    if (!wrap) return null;
    return normalizeSectionId(wrap.id);
  }

  /**
   * Section IDs for all theme sections inside `#MainContent`, in DOM order.
   * Used when the product URL changes (combined listings) so every product-scoped
   * block (reviews, metafields, app embeds, etc.) re-renders with the sibling product.
   * @returns {string[]}
   */
  #getMainContentSectionIds() {
    const main = document.getElementById("MainContent");
    if (!main) return [];

    return Array.from(main.querySelectorAll('.shopify-section[id^="shopify-section-"]')).map((el) =>
      normalizeSectionId(el.id),
    );
  }

  /**
   * @param {Record<string, string>} json
   * @param {string} sectionId
   * @returns {boolean}
   */
  #hasSectionHtml(json, sectionId) {
    const key = normalizeSectionId(sectionId);
    return json[key] != null || json[sectionId] != null;
  }

  /**
   * Morph all `sectionIds` for a sibling product URL: batch `?sections=` first,
   * then `section_id=` for any section omitted from the batch payload (app blocks,
   * etc.). Never navigates — callers decide whether to update history if at least
   * one section morphed.
   * @param {string[]} sectionIds
   * @param {{ url: URL, cache?: boolean, reject?: (oldNode: Node, newNode: Node) => boolean }} options
   * @param {number} token - Render token captured at click time; work stops as soon
   *   as a newer option change takes ownership of the DOM.
   * @returns {Promise<boolean>} True if at least one section morphed successfully
   */
  async #morphSectionsForCombinedListing(sectionIds, options, token) {
    let anyMorphed = false;
    /** @type {Record<string, string>|null} */
    let batchJson = null;

    try {
      batchJson = await sectionRenderer.renderSections(sectionIds, options);
      if (batchJson && sectionIds.some((id) => this.#hasSectionHtml(batchJson, id))) {
        anyMorphed = true;
      }
    } catch (err) {
      console.warn("[ProductSection] Combined listing batch section render failed:", err);
    }

    const missing =
      batchJson && typeof batchJson === "object"
        ? sectionIds.filter((id) => !this.#hasSectionHtml(batchJson, id))
        : [...sectionIds];

    for (const id of missing) {
      // A newer option change already aborted this batch's morphs and owns the
      // DOM — don't start fresh renders for the gaps and undo it.
      if (token !== this.renderToken) return false;

      try {
        // `null` means this section's request was cancelled rather than rendered.
        const html = await sectionRenderer.renderSection(id, options);
        if (html != null) anyMorphed = true;
      } catch (err) {
        console.warn(`[ProductSection] Combined listing section "${id}" render failed:`, err);
      }
    }

    return anyMorphed;
  }

  #getFetchUrl({ href, productUrl, optionValueIds = [] }) {
    const browserUrl = new URL(href || productUrl || window.location.href, window.location.origin);
    const fetchUrl = new URL(browserUrl.pathname, window.location.origin);

    if (optionValueIds.length) {
      fetchUrl.searchParams.set("option_values", optionValueIds.join(","));
    } else if (browserUrl.searchParams.has("variant")) {
      fetchUrl.searchParams.set("variant", browserUrl.searchParams.get("variant"));
    }

    return { browserUrl, fetchUrl };
  }

  #applyVariantPreview(variant, browserUrl) {
    if (!variant) return;

    const variantInput = this.element.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = variant.id;

    const priceEl = this.element.querySelector("[data-product-price]");
    if (priceEl && variant.price != null) {
      priceEl.textContent = formatMoney(variant.price);
    }

    const buyBoxPrice = this.element.querySelector("[data-buy-box-price]");
    const isOosMode =
      getThemeSettings().hideSalePriceWhenOos === true && buyBoxPrice?.dataset.productAvailable === "false";

    if (isOosMode) {
      // Liquid renders [data-compare-price] when compare_at > 0, [data-price] as fallback.
      const displayPrice = variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
      const priceEl = buyBoxPrice?.querySelector("[data-compare-price]") ?? buyBoxPrice?.querySelector("[data-price]");
      if (priceEl) {
        priceEl.textContent = formatMoney(displayPrice);
        priceEl.classList.remove("hidden");
      }
    } else {
      const compareEl = this.element.querySelector("[data-compare-price]");
      if (compareEl) {
        const hasCompare = variant.compare_at_price > variant.price;
        compareEl.textContent = hasCompare ? formatMoney(variant.compare_at_price) : "";
        compareEl.classList.toggle("hidden", !hasCompare);
      }
    }

    const atcBtn = this.element.querySelector("[data-add-to-cart]");
    if (atcBtn) {
      // Resolve the server-computed button type for this variant so pre-order is
      // synced (not just available/out-of-stock). Without this the pre-order
      // state lingers until the section morph, leaving any consumers of
      // [data-button-type] (labels, :has()-based visibility) stale in the meantime.
      const buttonType = this.#resolveButtonType(variant.id) ?? (variant.available ? "add-to-cart" : "out-of-stock");

      atcBtn.disabled = false;
      atcBtn.dataset.buttonType = buttonType;

      if (buttonType === "out-of-stock") {
        atcBtn.textContent = atcBtn.dataset.oosButtonText || "Sold Out - Get Notified";
        atcBtn.classList.add("klaviyo-bis-trigger");
      } else if (buttonType === "pre-order") {
        atcBtn.textContent = atcBtn.dataset.preorderButtonText || "Pre-Order";
        atcBtn.classList.remove("klaviyo-bis-trigger");
      } else {
        atcBtn.textContent = atcBtn.dataset.addToCartText || "Add to Cart";
        atcBtn.classList.remove("klaviyo-bis-trigger");
      }
    }

    this.#updateInventory(variant);

    history.replaceState({}, "", browserUrl);
  }

  #updateInventory(variant) {
    if (!variant) return;

    this.element.querySelectorAll("[data-buy-box-inventory]").forEach((inventoryEl) => {
      const inventoryByVariant = this.#readInventoryMap(inventoryEl);
      const inventory = inventoryByVariant[String(variant.id)];
      const hasInventoryRecord = inventory != null;
      const available =
        hasInventoryRecord && typeof inventory.available === "boolean"
          ? inventory.available
          : variant.available === true;
      const quantity = Number(
        hasInventoryRecord && inventory.inventory_quantity != null
          ? inventory.inventory_quantity
          : (variant.inventory_quantity ?? 0),
      );
      const configuredThreshold = Number(inventoryEl.dataset.inventoryMinimum || 10);
      const threshold = Number.isFinite(configuredThreshold) && configuredThreshold > 0 ? configuredThreshold : 10;
      const showInventory = available === true && quantity > 0 && quantity <= threshold;
      const contentEl = inventoryEl.querySelector(".inventory-counter-content");
      const messageEl = inventoryEl.querySelector(".inventory-message");
      const messageTemplate = this.#decodeInventoryMessageTemplate(
        inventoryEl.dataset.inventoryMessageTemplate || "Only [amount] left in stock!",
      );

      inventoryEl.setAttribute("aria-hidden", showInventory ? "false" : "true");
      if (showInventory) {
        inventoryEl.setAttribute("role", "status");
        inventoryEl.setAttribute("aria-live", "polite");
      } else {
        inventoryEl.removeAttribute("role");
        inventoryEl.removeAttribute("aria-live");
      }
      if (contentEl) {
        contentEl.style.display = showInventory ? "block" : "none";
      }
      if (messageEl) {
        messageEl.textContent = messageTemplate.replaceAll("[amount]", String(quantity));
      }
    });
  }

  #readInventoryMap(inventoryEl) {
    const scriptEl = inventoryEl.querySelector("[data-variant-inventory]");
    if (!scriptEl) return {};

    try {
      return JSON.parse(scriptEl.textContent || "{}");
    } catch {
      return {};
    }
  }

  /**
   * Look up the server-computed ATC button type ("add-to-cart" | "pre-order" |
   * "out-of-stock")
   * @param {number|string} variantId
   * @returns {string|null}
   */
  #resolveButtonType(variantId) {
    const mapEl = this.element.querySelector("[data-variant-button-states]");
    if (!mapEl) return null;

    try {
      const states = JSON.parse(mapEl.textContent || "{}");
      return states[String(variantId)] ?? null;
    } catch {
      return null;
    }
  }

  #decodeInventoryMessageTemplate(template) {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = String(template || "").trim();
    return textarea.value;
  }

  /**
   * Post-morph lifecycle: re-bind this section's JS from the now-morphed DOM, and
   * — only when the product itself changed (a combined-listing sibling) —
   * re-initialize Yotpo widgets. A same-product variant switch leaves reviews
   * untouched, so re-scanning Yotpo on every swatch click is pure main-thread
   * cost with no visible change; skip it.
   * @param {{ productChanged?: boolean }} [options]
   */
  #afterMorph({ productChanged = false } = {}) {
    if (productChanged && window.yotpoWidgetsContainer?.initWidgets) {
      window.yotpoWidgetsContainer.initWidgets();
    }

    this.reinit();
  }

  /**
   * Handle a combined listing option change by morphing sections from the sibling
   * product URL (full `href`) instead of full-page navigation.
   *
   * Note: option-value IDs from the previous product are intentionally not
   * forwarded — passing them to the sibling render makes Shopify pin foreign
   * values (e.g. a finish the new product doesn't carry) as selected, which
   * corrupts the buy-box, URL, and SKU on the morphed page. The clicked
   * swatch's href already encodes the correct target variant when one exists.
   * @param {{ href?: string, productUrl?: string, token?: number }} params - `token`
   *   is the render token captured by handleOptionChange; it defaults to the current
   *   one so a direct caller gets "this is the latest change" semantics.
   */
  handleCombinedListingChange({ href, productUrl, token = this.renderToken }) {
    const { browserUrl, fetchUrl } = this.#getFetchUrl({ href, productUrl, optionValueIds: [] });
    const fallbackUrl = browserUrl.toString();
    // The product itself is changing, so no reject predicate is passed here:
    // Yotpo widget containers reset to their pre-hydration state during the
    // morph, and #afterMorph() below calls initWidgets() to populate them
    // fresh for the new product.
    const renderOptions = { url: fetchUrl, cache: false };

    const primarySectionId = this.#getPrimarySectionId();
    const mainIds = this.#getMainContentSectionIds();
    const sectionIds = mainIds.length > 0 ? mainIds : primarySectionId ? [primarySectionId] : [];

    if (!sectionIds.length) {
      console.warn("[ProductSection] Combined listing: no MainContent sections to morph; falling back to navigation");
      window.location.href = fallbackUrl;
      return;
    }

    this.pendingSectionIds = sectionIds;

    void this.#morphSectionsForCombinedListing(sectionIds, renderOptions, token)
      .then((anyMorphed) => {
        // A newer click already owns the DOM: its own chain sets the URL and
        // re-initializes. `anyMorphed` only reports that HTML came back — the
        // morphs themselves were aborted — so without this a superseded batch
        // would still rewrite history and re-run Yotpo against stale markup.
        if (token !== this.renderToken) return;

        if (!anyMorphed) {
          console.warn("[ProductSection] Combined listing: no sections morphed; falling back to navigation");
          window.location.href = fallbackUrl;
          return;
        }
        // Build the canonical URL from the morphed section (authoritative)
        // rather than trusting the click-time href: pathname from the section's
        // data-product-handle, variant id from the resolved <input name="id">.
        // Both attributes are updated by morphdom before this runs, so we can
        // read them without waiting for #afterMorph()/reinit().
        // Update history BEFORE #afterMorph so components re-initialized in
        // reinit() (and integrations like Yotpo that key off window.location)
        // see the correct URL.
        const morphedHandle = this.element.dataset.productHandle;
        const resolvedId = this.element.querySelector('input[name="id"]')?.value;
        const finalUrl = new URL(fallbackUrl, window.location.origin);
        if (morphedHandle) {
          finalUrl.pathname = `/products/${morphedHandle}`;
        }
        if (resolvedId) {
          finalUrl.searchParams.set("variant", resolvedId);
        }
        history.replaceState({}, "", finalUrl.toString());
        this.#afterMorph({ productChanged: true });
      })
      .catch((err) => {
        if (token !== this.renderToken) return;
        console.warn("[ProductSection] Combined listing morph failed; falling back to navigation:", err);
        window.location.href = fallbackUrl;
      });
  }

  handleOptionChange({ href, productUrl, optionValueIds = [], variant = null }) {
    const sectionId = this.#getPrimarySectionId();

    if (!sectionId) {
      window.location.href = href || productUrl || window.location.href;
      return;
    }

    // Every click supersedes the one before it. Bump the token so any in-flight
    // chain stops short of its side effects, then abort the morphs it queued:
    // sectionRenderer aborts per section ID, so a same-product re-render would
    // otherwise cancel only the primary section of a combined-listing batch and
    // let the rest morph the page around it to the sibling product.
    const token = ++this.renderToken;
    if (this.pendingSectionIds.length) {
      sectionRenderer.abortSections(this.pendingSectionIds);
    }

    const { browserUrl, fetchUrl } = this.#getFetchUrl({ href, productUrl, optionValueIds });
    const isCombinedListing = browserUrl.pathname !== window.location.pathname;

    if (isCombinedListing) {
      this.handleCombinedListingChange({ href, productUrl, token });
      return;
    }

    this.pendingSectionIds = [sectionId];

    if (variant) {
      this.#applyVariantPreview(variant, browserUrl);
    }

    // Deliberately uncached: a cache hit would serve the variant's page-load /
    // first-visit HTML snapshot, so a mid-session server-side price, inventory, or
    // availability change wouldn't appear when revisiting a variant. Always
    // re-fetch so the morphed buy box reflects live state.
    sectionRenderer
      .renderSection(sectionId, { url: fetchUrl, cache: false, reject: preserveYotpoWidgetOnMorph })
      .then((html) => {
        if (token !== this.renderToken) return;
        // Cancelled before it rendered: the DOM is untouched, so don't rewrite the
        // URL or re-initialize as though a morph had landed.
        if (html == null) return;
        history.replaceState({}, "", browserUrl);
        this.#afterMorph();
      })
      .catch((err) => {
        // Don't navigate on behalf of a click the user has already moved past.
        if (token !== this.renderToken) return;
        console.warn("Product option morph failed, falling back to navigation:", err);
        window.location.href = browserUrl.toString();
      });
  }

  /**
   * Proxy the custom financing banner Shop Pay button to the real
   * installments CTA rendered inside the `shopify-payment-terms`
   * web component (shadow DOM).
   */
  #wireShopPayLearnMore() {
    this.shopPayWireRun += 1;
    const currentWireRun = this.shopPayWireRun;
    const proxyBtn = this.element.querySelector("[data-buy-box-shop-pay-button]");

    if (this.shopPayProxyBtn && this.shopPayProxyClickHandler) {
      this.shopPayProxyBtn.removeEventListener("click", this.shopPayProxyClickHandler);
      this.shopPayProxyBtn = null;
      this.shopPayProxyClickHandler = null;
    }

    if (!proxyBtn) return;
    if (typeof customElements === "undefined") return;

    const paymentTermsRoot = proxyBtn.closest(".finance-banner") || this.element;
    const queryShadowDeep = (root, selector) => {
      if (!root) return null;

      const directMatch = root.querySelector?.(selector);
      if (directMatch) return directMatch;

      const nodes = root.querySelectorAll?.("*") || [];
      for (const node of nodes) {
        const shadowMatch = queryShadowDeep(node.shadowRoot, selector);
        if (shadowMatch) return shadowMatch;
      }

      return null;
    };
    const getOriginalCta = () =>
      queryShadowDeep(paymentTermsRoot.querySelector("shopify-payment-terms")?.shadowRoot, "#shopify-installments-cta");

    const wireWhenReady = async () => {
      try {
        await customElements.whenDefined("shopify-payment-terms");
      } catch {
        // This custom element may never be defined on some PDP states.
        return;
      }

      // Give the components a few micro/animation frames to render shadow DOM.
      for (let attempt = 0; attempt < 30; attempt += 1) {
        if (currentWireRun !== this.shopPayWireRun) return;

        if (getOriginalCta()) {
          const clickHandler = (event) => {
            event.preventDefault();
            getOriginalCta()?.click();
          };
          proxyBtn.addEventListener("click", clickHandler);
          this.shopPayProxyBtn = proxyBtn;
          this.shopPayProxyClickHandler = clickHandler;
          proxyBtn.dataset.shopPayLearnMoreWired = "true";
          return;
        }

        await new Promise((resolve) => requestAnimationFrame(resolve));
      }
    };

    void wireWhenReady();
  }

  handleVariantChange(variant) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variant.id);
    this.handleOptionChange({
      href: url.toString(),
      productUrl: url.pathname,
      variant,
    });
  }
}

// Auto-initialize
document.querySelectorAll('[data-section-type="product-detail"]').forEach((el) => {
  new ProductSection(el);
});

